import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryState } from 'nuqs';
import { useQueryClient } from '@tanstack/react-query';
import { AccountPageHeader } from '../../account/components/AccountPageHeader';
import { Button, Input, Textarea, Label, RatingStarsInput, ImageGallery } from '@shopmuchmore/design-system';
import { toast } from 'sonner';
import { useProductReviewMutation, useProductDetailQuery } from '../../product/product.services';
import { ORDERS_QUERY_KEYS } from '../../orders/orders.services';
import { Loader2, Package } from 'lucide-react';

export function WriteReviewPage() {
    const [productSlug] = useQueryState('productSlug');
    const [productId] = useQueryState('productId');
    const [orderNo] = useQueryState('orderNo');
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Fetch product details - use productSlug or productId
    const { data: product, isLoading: isProductLoading, error: productError } = useProductDetailQuery(productSlug || productId || '', {
        enabled: !!productSlug || !!productId
    });

    // Redirect if missing params
    useEffect(() => {
        if ((!productSlug && !productId) || !orderNo) {
            navigate('/account/orders', { replace: true });
        }
    }, [productSlug, productId, orderNo, navigate]);

    const [rating, setRating] = useState(0);
    const [title, setTitle] = useState('');
    const [review, setReview] = useState('');
    const [images, setImages] = useState<(File | string)[]>([]);

    const mutation = useProductReviewMutation({
        onSuccess: () => {
            toast.success('Review submitted successfully!');
            
            // Invalidate the order detail query to update "reviewed" status
            if (orderNo) {
                queryClient.invalidateQueries({
                    queryKey: ORDERS_QUERY_KEYS.DETAIL(orderNo)
                });
            }
            
            navigate(`/account/orders/${orderNo}`); // Navigate back to order details
        },
        onError: (error) => {
            console.error('Review submission error:', error);
            const msg = (error as any)?.response?.data?.message || 'Failed to submit review';
            toast.error(msg);
        }
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (rating === 0) {
            toast.error('Please select a rating');
            return;
        }

        mutation.mutate({
            productSlug: productSlug || product?.product_slug || '',
            data: {
                title,
                review_text: review,
                rating,
                images: images.filter((img): img is File => img instanceof File)
            }
        });
    };

    if (isProductLoading) {
        return (
            <div className="w-full h-[60vh] flex flex-col items-center justify-center">
                <Loader2 className="w-10 h-10 text-secondary animate-spin mb-4" />
                <p className="text-gray-500 font-bold">Loading product details...</p>
            </div>
        );
    }

    if (productError || !product) {
        return (
            <div className="w-full py-20 text-center space-y-4">
                 <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Package className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-xl font-black text-gray-900">Product Not Found</h3>
                <p className="text-gray-500">We couldn't retrieve the product information for this review.</p>
                <Button onClick={() => navigate(-1)}>Go Back</Button>
            </div>
        );
    }

    return (
        <div className="w-full space-y-8">
            <AccountPageHeader
                title="Write a Review"
                description={`Share your thoughts on ${product.name}`}
            />

            <div className="bg-white rounded-xl border-[1.5px] border-gray-100 p-6 md:p-8 space-y-8">
                {/* Product Summary */}
                <div className="flex items-center gap-4 pb-6 border-b-[1.5px] border-gray-100">
                    <div className="w-16 h-16 bg-gray-50 rounded-lg overflow-hidden shrink-0 border-[1.5px] border-gray-100 flex items-center justify-center">
                        {(() => {
                            const productImage = product.main_thumbnail || 
                                               product.images?.find(img => img.is_primary)?.thumbnail || 
                                               product.images?.[0]?.thumbnail;
                            
                            return productImage ? (
                                <img src={productImage} alt={product.name} className="w-full h-full object-cover" />
                            ) : (
                                <Package className="w-6 h-6 text-gray-300" />
                            );
                        })()}
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900">{product.name}</h3>
                        <p className="text-sm text-gray-500 font-medium tracking-tight">Order #{orderNo}</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Rating */}
                    <div className="space-y-3">
                        <Label className="text-sm font-black uppercase tracking-wider text-gray-400">Overall Rating</Label>
                        <RatingStarsInput
                            value={rating}
                            onChange={setRating}
                            size="lg"
                        />
                        {rating > 0 && (
                            <p className="text-sm font-black text-amber-600 uppercase tracking-widest">
                                {rating === 5 ? 'Excellent!' : rating === 4 ? 'Very Good' : rating === 3 ? 'Average' : rating === 2 ? 'Poor' : 'Terrible'}
                            </p>
                        )}
                    </div>

                    {/* Title */}
                    <div className="space-y-3">
                        <Label htmlFor="title" className="text-sm font-black uppercase tracking-wider text-gray-400">Review Title</Label>
                        <Input
                            id="title"
                            placeholder="Summarize your experience (e.g., Amazing quality!)"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                            className="h-14 rounded-2xl border-gray-100"
                        />
                    </div>

                    {/* Review Body */}
                    <div className="space-y-3">
                        <Label htmlFor="review" className="text-sm font-black uppercase tracking-wider text-gray-400">Your Review (Optional)</Label>
                        <Textarea
                            id="review"
                            placeholder="What did you like or dislike? How was the quality? Did it meet your expectations?"
                            className="min-h-[180px] rounded-2xl border-gray-100 p-4"
                            value={review}
                            onChange={(e) => setReview(e.target.value)}
                        />
                        <p className="text-xs text-gray-500 text-right font-bold uppercase tracking-widest">{review.length} / 500 characters</p>
                    </div>

                    {/* Image Upload */}
                    <div className="space-y-3">
                        <Label className="text-sm font-black uppercase tracking-wider text-gray-400">Add Photos (Max 3)</Label>
                        <ImageGallery
                            images={images}
                            onChange={setImages}
                            maxImages={3}
                        />
                    </div>

                    {/* Actions */}
                    <div className="pt-6 flex items-center gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate(-1)}
                            disabled={mutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={mutation.isPending}
                        >
                            {mutation.isPending ? (
                                <span className="flex items-center gap-2">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Submitting
                                </span>
                            ) : 'Post Review'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
